import { graphql, Link } from "gatsby"
import React from "react"
import { SEOHead } from "components/SEO"
import Container from "components/ui/Container"
import Heading from "components/ui/Heading"
import Layout from "components/ui/Layout"

function CareersPage({ data, location: { pathname } }) {
  const jobs = data.allMarkdownRemark.nodes
  const hasOpenings = jobs.length > 0

  // Group jobs by department
  const jobsByDepartment = jobs.reduce((acc, job) => {
    const dept = job.frontmatter.department || "Other"
    if (!acc[dept]) {
      acc[dept] = []
    }
    acc[dept].push(job)
    return acc
  }, {})

  const departments = Object.keys(jobsByDepartment).sort()

  if (!hasOpenings) {
    return (
      <Layout
        headerFloating
        trackingName="Careers"
        pageName="careers"
        pagePath={pathname}
      >
        <Container section>
          <div className="flex min-h-screen flex-col items-center justify-center text-center">
            <Heading
              title="No positions currently open."
              subtitle="We're always looking for talented people. Send us your resume and let us know how you'd like to contribute."
            />
            <a
              href="mailto:careers@preset.io"
              className="mt-8 inline-block rounded-full bg-big-stone px-8 py-3 font-medium text-white transition-colors hover:bg-azure"
            >
              Get in Touch
            </a>
          </div>
        </Container>
      </Layout>
    )
  }

  return (
    <Layout
      headerFloating
      trackingName="Careers"
      pageName="careers"
      pagePath={pathname}
    >
      <Container section>
        <div className="pt-24 lg:pt-32">
          <Heading
            title="Join Our Team"
            subtitle="Help us make data exploration accessible to everyone. We're building the open analytics platform that's replacing legacy BI."
          />
        </div>

        <div className="mt-16 lg:mt-24">
          {departments.map((department) => (
            <div key={department} className="mb-12">
              <h2 className="text-2xl font-bold text-big-stone mb-6 border-b border-gray-200 pb-3">
                {department}
              </h2>
              <div className="space-y-4">
                {jobsByDepartment[department].map((job) => (
                  <Link
                    key={job.id}
                    to={`/careers/${job.frontmatter.slug}/`}
                    className="block p-6 bg-white rounded-lg border border-gray-200 hover:border-azure hover:shadow-md transition-all"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div>
                        <h3 className="text-xl font-semibold text-big-stone hover:text-azure">
                          {job.frontmatter.title}
                        </h3>
                        <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-600">
                          <span>{job.frontmatter.location}</span>
                          <span className="text-gray-300">|</span>
                          <span>{job.frontmatter.type}</span>
                        </div>
                      </div>
                      <div className="mt-4 md:mt-0">
                        <span className="text-azure font-medium">
                          View Position →
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 lg:mt-24 text-center py-12 bg-big-stone rounded-lg">
          <h2 className="text-2xl font-bold text-white mb-4">
            Don&apos;t see the right role?
          </h2>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            We&apos;re always looking for talented people. Send us your resume
            and let us know how you&apos;d like to contribute.
          </p>
          <a
            href="mailto:careers@preset.io"
            className="inline-block px-6 py-3 bg-white text-big-stone font-medium rounded-full hover:bg-gray-100 transition-colors"
          >
            Get in Touch
          </a>
        </div>
      </Container>
    </Layout>
  )
}

export function Head() {
  return (
    <SEOHead
      title="Careers"
      description="Join the Preset team and help make data exploration accessible to everyone. View our open positions and apply today."
    />
  )
}

export default CareersPage

export const pageQuery = graphql`
  query CareersPageQuery {
    allMarkdownRemark(
      filter: {
        fileAbsolutePath: { regex: "/content/jobs/" }
        frontmatter: { published: { eq: true } }
      }
      sort: { frontmatter: { department: ASC } }
    ) {
      nodes {
        id
        frontmatter {
          title
          department
          location
          type
          slug
        }
      }
    }
  }
`
